import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  AlignLeft,
  BarChart3,
  X,
  CircleDot,
  ListChecks,
  MessageSquareText,
  PlusSquare,
  Rows3,
  Star,
  ToggleLeft,
  Type,
  Table2,
} from 'lucide-react';

import css from 'src/utils/css';
import {
  getQuestionTypeOptions,
  FORMULARIO_GLOBAL_QUESTION_TYPES,
} from './FormularioGlobalQuestionTypes';

const classes = css`
  .menu {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 22px 26px;
  }

  .trigger {
    align-items: center;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #0f172a;
    display: flex;
    gap: 12px;
    min-height: 72px;
    padding: 0 26px;
    text-align: left;
    width: 100%;
  }

  .triggers {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .trigger:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }

  .trigger:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .triggerIcon {
    align-items: center;
    color: #0f7180;
    display: inline-flex;
    flex: 0 0 auto;
    justify-content: center;
  }

  .title {
    align-items: center;
    color: #475569;
    display: flex;
    font-size: 13px;
    font-weight: 700;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .cancel {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #64748b;
    display: inline-flex;
    font-size: 12px;
    gap: 6px;
    padding: 6px 8px;
  }

  .cancel:hover {
    background: #f1f5f9;
    color: #0f7180;
  }

  .grid {
    display: grid;
    gap: 14px 22px;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  }

  .item {
    align-items: flex-start;
    background: transparent;
    border: 0;
    border-radius: 8px;
    color: #0f172a;
    display: flex;
    gap: 12px;
    padding: 10px;
    text-align: left;
  }

  .item:hover {
    background: #f8fafc;
  }

  .item:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .icon {
    align-items: center;
    background: #ecfeff;
    border-radius: 8px;
    color: #0f7180;
    display: inline-flex;
    flex: 0 0 auto;
    height: 32px;
    justify-content: center;
    width: 32px;
  }

  .label {
    color: #0f172a;
    display: block;
    font-size: 14px;
    font-weight: 800;
    line-height: 18px;
  }

  .description {
    color: #64748b;
    display: block;
    font-size: 12px;
    line-height: 16px;
    margin-top: 2px;
  }
`;

const iconByQuestionType = {
  [FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO]: Table2,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA]: CircleDot,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA]: ListChecks,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.TEXTO_PARAGRAFO]: AlignLeft,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS]: Star,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_LIKERT]: BarChart3,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_CURTA]: Type,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.NPS]: MessageSquareText,
  [FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO]: ToggleLeft,
};

const FormularioGlobalAddMenu = ({
  disabled,
  includeSection = false,
  onAddQuestion,
  onAddSection,
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const questionTypes = getQuestionTypeOptions(intl);

  const handleAddSection = () => {
    onAddSection && onAddSection();
    setExpanded(false);
  };

  const handleAddQuestion = (type) => {
    onAddQuestion && onAddQuestion(type);
    setExpanded(false);
  };

  if (!expanded) {
    return (
      <div className={classes.triggers}>
        <button
          className={classes.trigger}
          disabled={disabled}
          onClick={() => setExpanded(true)}
          type="button"
        >
          <span className={classes.triggerIcon}>
            <PlusSquare size={20} />
          </span>
          <span className={classes.label}>
            {intl.formatMessage({
              id: 'labelAdicionarPergunta',
            })}
          </span>
        </button>
        {includeSection && (
          <button
            className={classes.trigger}
            disabled={disabled}
            onClick={handleAddSection}
            type="button"
          >
            <span className={classes.triggerIcon}>
              <Rows3 size={20} />
            </span>
            <span className={classes.label}>
              {intl.formatMessage({
                id: 'labelAdicionarSecao',
              })}
            </span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={classes.menu}>
      <div className={classes.title}>
        <span>
          {includeSection
            ? intl.formatMessage({
                id: 'labelEscolhaOQueAdicionar',
              })
            : intl.formatMessage({
                id: 'labelAdicionarPergunta',
              })}
        </span>
        <button
          className={classes.cancel}
          onClick={() => setExpanded(false)}
          type="button"
        >
          <X size={14} />
          {intl.formatMessage({
            id: 'label.cancelar',
          })}
        </button>
      </div>
      <div className={classes.grid}>
        {questionTypes.map((type) => {
          const Icon = iconByQuestionType[type.id] || PlusSquare;

          return (
            <button
              className={classes.item}
              disabled={disabled}
              key={type.id}
              onClick={() => handleAddQuestion(type.id)}
              type="button"
            >
              <span className={classes.icon}>
                <Icon size={18} />
              </span>
              <span>
                <span className={classes.label}>{type.label}</span>
                <span className={classes.description}>{type.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FormularioGlobalAddMenu;
